import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Checkbox, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconClock, IconSignature, IconTruckDelivery } from '@tabler/icons-react';
import { useConfirmDelivery } from '@/hooks/useDeals';
import { forwardingPhase } from '@/lib/forwarding';
import { pendingUpdCount } from '@/lib/forwardingView';
import type { BargainStepsInfo } from '@/types/deals';

interface ForwardingActionZoneProps {
  readonly info: BargainStepsInfo;
  readonly dealId: number;
  /** Переход к подписи УПД (вкладка «Документы»). */
  readonly onGoToSign: () => void;
}

// Закреплённая зона действий шага «Перевозка»: то, что нельзя пропустить — подпись УПД и подтверждение
// получения. Показывается только в живом шаге. Если действий/статусов нет — null.
export default function ForwardingActionZone({ info, dealId, onGoToSign }: ForwardingActionZoneProps) {
  const { t } = useTranslation();
  const phase = forwardingPhase(info.processingStep ?? '');
  const updPending = pendingUpdCount(info);
  const isBuyer = info.isBuyer === true;

  const [received, setReceived] = useState(false);
  const confirm = useConfirmDelivery(dealId);

  const showUpd = updPending > 0;
  const showDelivery = phase === 'waitDelivery' && isBuyer;
  const showModeration = phase === 'weightModeration';

  if (!showUpd && !showDelivery && !showModeration) {
    return null;
  }

  return (
    <Stack gap="sm">
      {showUpd ? (
        <Alert color="brand" variant="light" icon={<IconSignature size={18} />} py="xs">
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Text fz="sm" fw={500}>
              {t('app.deals.forwarding.actionSignUpd', { count: updPending })}
            </Text>
            <Button size="compact-sm" variant="white" onClick={onGoToSign}>
              {t('app.deals.forwarding.actionGoToSign')}
            </Button>
          </Group>
        </Alert>
      ) : null}

      {showDelivery ? (
        <Alert color="brand" variant="light" icon={<IconTruckDelivery size={18} />}>
          <Stack gap="xs">
            <Text fz="sm" fw={500}>
              {t('app.deals.forwarding.waitBuyerDelivery')}
            </Text>
            <Checkbox
              checked={received}
              onChange={(event) => setReceived(event.currentTarget.checked)}
              label={t('app.deals.forwarding.receivedLabel')}
              disabled={confirm.isPending}
            />
            <Button
              size="compact-sm"
              leftSection={<IconTruckDelivery size={16} />}
              disabled={!received || confirm.isPending}
              loading={confirm.isPending}
              onClick={() => confirm.mutate()}
              w="fit-content"
            >
              {t('app.deals.forwarding.confirm')}
            </Button>
          </Stack>
        </Alert>
      ) : null}

      {showModeration ? (
        <Group gap="xs" wrap="nowrap">
          <ThemeIcon size={28} radius="xl" variant="light" color="brand">
            <IconClock size={16} />
          </ThemeIcon>
          <Text fz="sm">{t('app.deals.forwarding.weightModeration')}</Text>
        </Group>
      ) : null}
    </Stack>
  );
}
